import type { ReactNode } from "react"

interface HomeHeroProps {
  /** h1 de la página: el saludo. */
  greeting: string
  subtitle?: ReactNode
  /** Portada que va de fondo; sin ella el hero es solo texto sobre el azul de la marca. */
  cover?: string
  /** h2: lo que el estudiante hace ahora (el curso a retomar o el primero que puede empezar). */
  heading: string
  text: ReactNode
  /** Avance de 0 a 100 del curso a retomar: una línea fina en el borde inferior. */
  progress?: { value: number; label: string }
  /** Acciones (botones o enlaces). */
  children: ReactNode
}

// Hero de Inicio: sin caja ni borde. La portada ocupa todo el ancho del área de contenido
// (sale del relleno de <main> con márgenes negativos), el degradado la funde con el azul
// de la marca donde va el texto y con la página en el borde inferior.
export function HomeHero({ greeting, subtitle, cover, heading, text, progress, children }: HomeHeroProps) {
  return (
    <section
      aria-labelledby="hero-titulo"
      className="relative isolate -mx-4 -mt-4 overflow-hidden sm:-mx-6 sm:-mt-6 lg:-mx-8 lg:-mt-8"
    >
      {cover && <img src={cover} alt="" className="absolute inset-0 -z-20 h-full w-full object-cover opacity-30 lg:opacity-100" />}
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 bg-linear-to-t from-background via-background/90 to-background/80 lg:bg-linear-to-r lg:from-background lg:via-background/85 lg:to-background/10"
      />
      <div aria-hidden="true" className="absolute inset-x-0 bottom-0 -z-10 h-24 bg-linear-to-t from-background to-transparent" />

      <div className="px-4 py-14 sm:px-6 sm:py-20 lg:px-8 lg:py-28">
        <div className="max-w-2xl">
          <h1
            id="hero-titulo"
            className="text-balance font-display text-3xl font-bold leading-tight tracking-tight text-foreground sm:text-5xl"
          >
            {greeting}
          </h1>
          {subtitle && <p className="mt-2 text-sm tabular-nums text-muted-foreground">{subtitle}</p>}

          <h2 className="mt-8 text-balance text-xl font-semibold tracking-tight text-foreground sm:text-2xl">{heading}</h2>
          <p className="mt-2 max-w-prose text-base text-muted-foreground">{text}</p>

          <div className="mt-8 flex flex-wrap items-center gap-3">{children}</div>
        </div>
      </div>

      {progress && (
        <div
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={progress.value}
          aria-label={progress.label}
          className="absolute inset-x-0 bottom-0 h-1 bg-black/40"
        >
          <div
            className="motion-progress motion-progress-in h-full w-full bg-primary"
            style={{ transform: `scaleX(${progress.value / 100})` }}
          />
        </div>
      )}
    </section>
  )
}
